import { useState } from 'react'
import {
  ActionIcon,
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { DatePickerInput } from '@mantine/dates'
import type { DateValue } from '@mantine/dates'
import {
  IconCalendar,
  IconCopy,
  IconDownload,
  IconRestore,
  IconSettings,
} from '@tabler/icons-react'
import { cn } from '@/utils/cn'
import { costEmphasisClass, formatUsd } from '../cost'
import type {
  AssistantConversationResponse,
  AssistantDebugSelection,
  AssistantProviderName,
  AssistantProviderOption,
} from '../useAssistant'

type AssistantDebugSettingsProps = {
  providers: AssistantProviderOption[]
  conversationId: string | null
  conversationKey: string | null
  currentSelection: AssistantDebugSelection
  selectedSelection: AssistantDebugSelection
  revealConversationDetails: boolean
  // Rolled-up conversation cost estimate (USD), shown next to the reveal switch
  // while details are revealed. Undefined when no priced usage has been recorded.
  conversationCostUsd?: number
  forceTodayDate: boolean
  forcedTodayDate: DateValue
  actors: string[]
  selectedActor: string | null
  selfActor: string | null
  showActorPicker: boolean
  onActorChange: (actor: string) => void
  onProviderChange: (provider: AssistantProviderName | null) => void
  onModelChange: (model: string | null) => void
  onRevealConversationDetailsChange: (value: boolean) => void
  onForceTodayDateChange: (value: boolean) => void
  onForcedTodayDateChange: (value: DateValue) => void
  onDownloadConversation: () => Promise<AssistantConversationResponse | null>
}

export const AssistantDebugSettings = ({
  providers,
  conversationId,
  conversationKey,
  currentSelection,
  selectedSelection,
  revealConversationDetails,
  conversationCostUsd,
  forceTodayDate,
  forcedTodayDate,
  actors,
  selectedActor,
  selfActor,
  showActorPicker,
  onActorChange,
  onProviderChange,
  onModelChange,
  onRevealConversationDetailsChange,
  onForceTodayDateChange,
  onForcedTodayDateChange,
  onDownloadConversation,
}: AssistantDebugSettingsProps) => {
  const [opened, setOpened] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const providerOptions = providers.map((item) => ({
    value: item.provider,
    label: item.provider,
  }))
  const modelOptions =
    providers
      .find((item) => item.provider === selectedSelection.provider)
      ?.models.map((model) => ({ value: model, label: model })) ?? []
  const providerChanged = selectedSelection.provider !== currentSelection.provider
  // The hook merges the user's own reference into `actors`; the guard here
  // only covers the moment a non-self actor is selected but the list hasn't
  // loaded yet, so the Select never shows an empty value. The caller's own
  // reference is labeled so it stands out among raw reference strings.
  const actorValues =
    selectedActor && !actors.includes(selectedActor) ? [selectedActor, ...actors] : actors
  const actorOptions = actorValues.map((actor) => ({
    value: actor,
    label: actor === selfActor ? `${actor} (you)` : actor,
  }))
  const handleCopyConversationKey = async (): Promise<void> => {
    if (!conversationKey) return
    try {
      await copyTextToClipboard(conversationKey)
      showNotification({ color: 'green', message: 'Copied conversation key' })
    } catch {
      showNotification({ color: 'red', message: 'Failed to copy conversation key' })
    }
  }
  const handleDownloadConversation = async (): Promise<void> => {
    setDownloading(true)
    try {
      const conversation = await onDownloadConversation()
      if (conversation) {
        downloadConversation(conversation)
      }
    } finally {
      setDownloading(false)
    }
  }

  return (
    <>
      <Group gap="xs" wrap="nowrap">
        {revealConversationDetails && conversationCostUsd !== undefined && (
          <Tooltip label="Total conversation cost (estimate)">
            <span className={cn('text-sm', costEmphasisClass(conversationCostUsd))}>
              {formatUsd(conversationCostUsd)}
            </span>
          </Tooltip>
        )}
        <Tooltip label="Reveal conversation details">
          <Switch
            aria-label="Reveal conversation details"
            size="sm"
            checked={revealConversationDetails}
            onChange={(event) => onRevealConversationDetailsChange(event.currentTarget.checked)}
          />
        </Tooltip>
        <Tooltip label="Assistant settings">
          <ActionIcon
            aria-label="Assistant settings"
            variant="light"
            size="sm"
            onClick={() => setOpened(true)}
          >
            <IconSettings size={15} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <Modal opened={opened} onClose={() => setOpened(false)} title="Assistant settings" centered>
        <Stack gap="sm">
          <Divider label="for current conversation" labelPosition="center" />
          <div>
            <Text size="sm" fw={500} mb={4}>
              key
            </Text>
            <Group align="flex-start" gap="xs" wrap="nowrap">
              <div className="min-h-9 min-w-0 flex-1 rounded border border-[#ced4da] bg-[#f8f9fa] px-3 py-2 text-sm break-all text-slate-700">
                {conversationKey ?? 'Unavailable'}
              </div>
              <Tooltip label="Copy key">
                <ActionIcon
                  className="mt-0.5"
                  aria-label="Copy conversation key"
                  variant="subtle"
                  size="md"
                  disabled={!conversationKey}
                  onClick={() => void handleCopyConversationKey()}
                >
                  <IconCopy size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </div>
          <Switch
            label="Reveal conversation details"
            checked={revealConversationDetails}
            onChange={(event) => onRevealConversationDetailsChange(event.currentTarget.checked)}
          />
          <Button
            variant="light"
            leftSection={<IconDownload size={16} />}
            onClick={() => void handleDownloadConversation()}
            disabled={!conversationId}
            loading={downloading}
          >
            Download
          </Button>
          <Divider label="for new conversations" labelPosition="center" />
          <Group align="flex-end" gap="xs" wrap="nowrap">
            <Select
              className="min-w-0 flex-1"
              label="Provider"
              data={providerOptions}
              value={selectedSelection.provider}
              onChange={(value) => onProviderChange(value as AssistantProviderName | null)}
              disabled={providerOptions.length === 0}
              allowDeselect={false}
              nothingFoundMessage="No providers"
            />
            <Tooltip label="Revert provider">
              <ActionIcon
                aria-label="Revert provider"
                variant="subtle"
                disabled={
                  !currentSelection.provider ||
                  selectedSelection.provider === currentSelection.provider
                }
                onClick={() => onProviderChange(currentSelection.provider)}
              >
                <IconRestore size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Group align="flex-end" gap="xs" wrap="nowrap">
            <Select
              className="min-w-0 flex-1"
              label="Model"
              data={modelOptions}
              value={selectedSelection.model}
              onChange={onModelChange}
              disabled={!selectedSelection.provider || modelOptions.length === 0}
              allowDeselect={false}
              nothingFoundMessage="No models"
            />
            <Tooltip label="Revert model">
              <ActionIcon
                aria-label="Revert model"
                variant="subtle"
                disabled={
                  providerChanged ||
                  !currentSelection.model ||
                  selectedSelection.model === currentSelection.model
                }
                onClick={() => onModelChange(currentSelection.model)}
              >
                <IconRestore size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Group align="flex-end" gap="xs" wrap="nowrap">
            <Checkbox
              className="pb-2"
              label="Force today's date to"
              checked={forceTodayDate}
              onChange={(event) => onForceTodayDateChange(event.currentTarget.checked)}
            />
            <DatePickerInput
              className="min-w-0 flex-1"
              placeholder="MM/DD/YYYY"
              value={forcedTodayDate}
              onChange={onForcedTodayDateChange}
              valueFormat="MM/DD/YYYY"
              clearable={false}
              disabled={!forceTodayDate}
              popoverProps={{ withinPortal: false }}
              rightSection={<IconCalendar size={16} />}
              rightSectionPointerEvents="none"
            />
          </Group>
          {showActorPicker && (
            <>
              <Divider label="for actor" labelPosition="center" />
              <Select
                label="Actor"
                data={actorOptions}
                value={selectedActor}
                onChange={(value) => {
                  if (!value) return
                  onActorChange(value)
                  // Picking an actor reloads the whole assistant view — close
                  // the settings dialog so the switch is immediately visible.
                  setOpened(false)
                }}
                searchable
                allowDeselect={false}
                nothingFoundMessage="No actors"
                comboboxProps={{ withinPortal: false }}
              />
            </>
          )}
        </Stack>
      </Modal>
    </>
  )
}

function downloadConversation(conversation: unknown): void {
  const blob = new Blob([`${JSON.stringify(conversation, null, 2)}\n`], {
    type: 'application/json;charset=utf-8',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${formatDownloadDate(new Date())}-assistant-conversation.json`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function formatDownloadDate(value: Date): string {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

async function copyTextToClipboard(text: string): Promise<void> {
  await navigator.clipboard.writeText(text)
}
